import type { TourStepTooltipConfig } from '@carboncopyco/types';
import {
  AnalyticsTrackEventName,
  type TrackEventProperties,
  useAnalytics,
} from '@carboncopyco/utils';
import { useDebouncedCallback } from 'use-debounce';

interface Props {
  tourId: string;
  activeFrameId: number;
}

const useEditorTrackTooltipEvents = ({ tourId, activeFrameId }: Props) => {
  const { trackEvent } = useAnalytics();

  const handleTooltipTrackEvents = useDebouncedCallback(
    (
      stepId: string,
      changedKeys: string[],
      newTooltipConfig: TourStepTooltipConfig
    ) => {
      let eventTitle = '';
      let variables: TrackEventProperties['variables'];
      const isButtonsNavigationChanged = changedKeys.some(
        (key) => key === 'navigationOptions.isButtonsNavigation'
      );
      const isButtonsNavigation =
        newTooltipConfig.navigationOptions.isButtonsNavigation;
      const isElementNavigationChanged = changedKeys.some(
        (key) => key === 'navigationOptions.isElementNavigation'
      );
      const isArrowToggleChanged = changedKeys.some(
        (key) => key === 'balloon.showArrow'
      );

      const isColorAndShapeChanged = changedKeys.some((key) =>
        key.startsWith('balloon.colorAndShape')
      );

      const isShadowChanged = changedKeys.some(
        (key) =>
          key.startsWith('balloon.boxShadow') &&
          key !== 'balloon.boxShadow.isActive'
      );

      const isUseDropshadowChanged = changedKeys.some(
        (key) => key === 'balloon.boxShadow.isActive'
      );

      const isHighlightActiveChanged = changedKeys.some(
        (key) => key === 'highlightElementOptions.isActive'
      );
      const isHighlightColorChanged = changedKeys.some((key) =>
        key.startsWith('highlightElementOptions.color')
      );
      const isSpotlightActiveChanged = changedKeys.some(
        (key) => key === 'spotlightOptions.isActive'
      );
      const isSpotlightColorChanged = changedKeys.some((key) =>
        key.startsWith('spotlightOptions.color')
      );
      const isStepCountStyleChanged = changedKeys.some((key) =>
        key.startsWith('text.stepCount')
      );

      const isNavigateToChanged = changedKeys.some((key) =>
        key.startsWith('navigationOptions.elementAction')
      );

      const isLayoutChanged = changedKeys.some((key) =>
        key.startsWith('balloon.layout')
      );

      const isElementNavigation =
        newTooltipConfig.navigationOptions.isElementNavigation;
      const isArrowToggleActive = newTooltipConfig.balloon.showArrow;
      const isHighlightActive =
        newTooltipConfig.highlightElementOptions.isActive;
      const isSpotlightActive = newTooltipConfig.spotlightOptions.isActive;

      const isUseDropdownShadowActive =
        newTooltipConfig.balloon.boxShadow.isActive;

      const navigateTo = newTooltipConfig.navigationOptions.elementAction.name;

      if (isButtonsNavigationChanged && isElementNavigationChanged) {
        eventTitle = isButtonsNavigation
          ? 'platform.tour.buttons_navigation_on_triggered_by_element_off'
          : 'platform.tour.element_navigation_on_triggered_by_buttons_off';
      }
      if (isButtonsNavigationChanged && !eventTitle) {
        eventTitle = isButtonsNavigation
          ? AnalyticsTrackEventName.TOUR_TOGGLE_BUTTONS_NAVIGATION_ON
          : AnalyticsTrackEventName.TOUR_TOGGLE_BUTTONS_NAVIGATION_OFF;
      }
      if (isElementNavigationChanged && !eventTitle) {
        eventTitle = isElementNavigation
          ? AnalyticsTrackEventName.TOUR_TOGGLE_ELEMENT_NAVIGATION_ON
          : AnalyticsTrackEventName.TOUR_TOGGLE_ELEMENT_NAVIGATION_OFF;
      }
      if (isArrowToggleChanged && !eventTitle) {
        eventTitle = isArrowToggleActive
          ? AnalyticsTrackEventName.TOUR_TOGGLE_ARROW_ON
          : AnalyticsTrackEventName.TOUR_TOGGLE_ARROW_OFF;
      }
      if (isHighlightActiveChanged) {
        eventTitle = isHighlightActive
          ? AnalyticsTrackEventName.TOUR_TOGGLE_HIGHLIGHT_ON
          : AnalyticsTrackEventName.TOUR_TOGGLE_HIGHLIGHT_OFF;
      }
      if (isHighlightColorChanged) {
        eventTitle = AnalyticsTrackEventName.TOUR_CHANGE_HIGHLIGHT_STYLE;
      }
      if (isSpotlightActiveChanged) {
        eventTitle = isSpotlightActive
          ? AnalyticsTrackEventName.TOUR_TOGGLE_SPOTLIGHT_ON
          : AnalyticsTrackEventName.TOUR_TOGGLE_SPOTLIGHT_OFF;
      }
      if (isSpotlightColorChanged) {
        eventTitle = AnalyticsTrackEventName.TOUR_CHANGE_SPOTLIGHT_STYLE;
      }
      if (isStepCountStyleChanged) {
        eventTitle = AnalyticsTrackEventName.TOUR_CHANGE_STYLE_STEPS_COUNTER;
        variables = changedKeys;
      }

      if (isColorAndShapeChanged) {
        eventTitle =
          AnalyticsTrackEventName.TOUR_CHANGE_TOOLTIP_COLOR_AND_SHAPE_STYLE;
        variables = changedKeys;
      }

      if (isShadowChanged) {
        eventTitle = AnalyticsTrackEventName.TOUR_CHANGE_TOOLTIP_SHADOW_STYLE;
        variables = changedKeys;
      }

      if (isUseDropshadowChanged) {
        eventTitle =
          AnalyticsTrackEventName.TOUR_TOGGLE_TOOLTIP_USE_DROP_SHADOW;
        variables = {
          isActive: isUseDropdownShadowActive,
        };
      }

      if (isNavigateToChanged) {
        eventTitle =
          AnalyticsTrackEventName.TOUR_CLICK_TOOLTIP_ELEMENT_NAVIGATION_NAVIGATE_TO;

        variables = {
          navigateTo: navigateTo,
        };
      }

      if (isLayoutChanged) {
        eventTitle = AnalyticsTrackEventName.TOUR_CLICK_TOOLTIP_LAYOUT;
        variables = {
          relation: newTooltipConfig.balloon.layout.relation,
          alignment: newTooltipConfig.balloon.layout.alignment,
        };
      }

      if (eventTitle) {
        trackEvent(eventTitle, {
          location: 'tour_editor_menu_floating_tooltip_editor_modal',
          tourId,
          stepId,
          variables,
          frameId: activeFrameId,
          timestamp: Date.now(),
        });
      }
    },
    500
  );

  return {
    handleTooltipTrackEvents,
  };
};

export default useEditorTrackTooltipEvents;
